import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { toCoreApiMutationResult } from '@/utils/core-api-mutation-result';
import { serverFetch } from '@/utils/server-fetch';
import { SSO_CLAIMED_DOMAIN_REGEX } from '@common/utils';

import type {
  SettingsActor,
  SettingsMutationResult,
} from './settings-access.server';

import { can, rolesFromRecords } from './permissions';
import {
  guardedMutation,
  loadSettingsActor,
  requireSettingsPermission,
} from './settings-access.server';

/**
 * Organization settings for the active organization: details, teams,
 * members, invitations and SSO. Ported from the legacy organization settings
 * pages, which called these same core-api routes through the website; the
 * permission checks are new (see `settings-access.server.ts`).
 */

const objectId = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid id');
const orgPath = (actor: SettingsActor) =>
  `/organizations/${encodeURIComponent(actor.organizationId)}`;

async function fetchJson(path: string, userId: string): Promise<unknown> {
  const response = await serverFetch({ path, userId });
  if (!response.ok) {
    throw new Error(`core-api ${path} responded ${response.status}`);
  }
  return response.json();
}

function parseList<T>(json: unknown, schema: z.ZodType<T>): T[] {
  return (Array.isArray(json) ? json : []).flatMap((item) => {
    const parsed = schema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  });
}

const byFullName = (
  a: { firstName: string; lastName: string },
  b: { firstName: string; lastName: string },
) =>
  `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);

// Organization details

export interface OrganizationAddress {
  id?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
  formattedAddress: string;
  location?: { latitude: number; longitude: number };
  unit?: string;
}

export interface OrganizationDetails {
  organizationId: string;
  name: string;
  website: string;
  imageUrl?: string;
  address: OrganizationAddress | null;
  /** Extra lines printed on receipts, as legacy's title/value pairs. */
  receiptInfo: { title: string; value: string }[];
  canEdit: boolean;
  canDelete: boolean;
}

const rawAddressSchema = z
  .object({
    id: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    zipcode: z.string().nullish(),
    country: z.string().nullish(),
    formattedAddress: z.string().nullish(),
    unit: z.string().nullish(),
    location: z
      .object({ latitude: z.number(), longitude: z.number() })
      .nullish()
      .catch(undefined),
  })
  .nullish()
  .catch(undefined);

const rawDetailsSchema = z.object({
  name: z.string().nullish(),
  website: z.string().nullish(),
  imageUrl: z.string().nullish(),
  location: rawAddressSchema,
  additionalReceiptInfo: z
    .record(z.string(), z.string())
    .nullish()
    .catch(undefined),
});

export const getOrganizationDetails = createServerFn().handler(
  async (): Promise<OrganizationDetails> => {
    const actor = await loadSettingsActor();
    const raw = rawDetailsSchema.parse(actor.organization);
    const location = raw.location;
    return {
      organizationId: actor.organizationId,
      name: raw.name ?? '',
      website: raw.website ?? '',
      imageUrl: raw.imageUrl || undefined,
      address:
        location && location.formattedAddress
          ? {
              id: location.id || undefined,
              city: location.city || undefined,
              state: location.state || undefined,
              zipcode: location.zipcode || undefined,
              country: location.country || undefined,
              formattedAddress: location.formattedAddress,
              location: location.location || undefined,
              unit: location.unit || undefined,
            }
          : null,
      receiptInfo: Object.entries(raw.additionalReceiptInfo ?? {}).map(
        ([title, value]) => ({ title, value }),
      ),
      canEdit: can('editOrgDetails', actor.roles),
      canDelete: can('deleteOrg', actor.roles),
    };
  },
);

const addressSchema = z.object({
  id: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipcode: z.string().optional(),
  country: z.string().optional(),
  formattedAddress: z.string().min(1),
  location: z
    .object({ latitude: z.number(), longitude: z.number() })
    .optional(),
  unit: z.string().trim().max(50).optional(),
});

export const organizationDetailsSchema = z.object({
  name: z.string().trim().min(1).max(100),
  website: z.string().trim().max(500),
  address: addressSchema,
  /** Legacy allowed up to 8 pairs. */
  receiptInfo: z
    .array(
      z.object({
        title: z.string().trim().min(1).max(100),
        value: z.string().trim().min(1).max(500),
      }),
    )
    .max(8),
});

export const updateOrganizationDetails = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof organizationDetailsSchema>) =>
    organizationDetailsSchema.parse(data),
  )
  .handler(({ data }) =>
    guardedMutation('updateOrganizationDetails', async () => {
      const actor = await requireSettingsPermission('editOrgDetails');
      const { unit, ...address } = data.address;
      // Legacy stored the unit on the location and printed it into the
      // address used on receipts.
      const fullFormattedAddress = unit
        ? address.formattedAddress.replace(
            /^([^,]*)/,
            (firstPart) => `${firstPart} ${unit}`,
          )
        : address.formattedAddress;
      const response = await serverFetch({
        path: orgPath(actor),
        method: 'PATCH',
        userId: actor.userId,
        body: JSON.stringify({
          name: data.name,
          website: data.website,
          location: { ...address, unit, fullFormattedAddress },
          additionalReceiptInfo: Object.fromEntries(
            data.receiptInfo.map(({ title, value }) => [title, value]),
          ),
        }),
      });
      return toCoreApiMutationResult(response, 'updateOrganizationDetails');
    }),
  );

export const deleteOrganization = createServerFn({ method: 'POST' }).handler(
  () =>
    guardedMutation('deleteOrganization', async () => {
      const actor = await requireSettingsPermission('deleteOrg');
      const response = await serverFetch({
        path: orgPath(actor),
        method: 'DELETE',
        userId: actor.userId,
      });
      return toCoreApiMutationResult(response, 'deleteOrganization');
    }),
);

// Browse teams

export interface BrowseTeam {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  memberCount: number;
  ownerName?: string;
  canAddMembers: boolean;
  canManageMembers: boolean;
}

export interface BrowseTeams {
  teams: BrowseTeam[];
  privateTeams: boolean;
  canChangePrivacy: boolean;
}

const rawSearchedTeamSchema = z.object({
  id: z.string().optional(),
  _id: z.string().optional(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  imageUrl: z.string().nullish(),
  memberCount: z.number().nullish(),
  members: z
    .array(
      z.object({
        userId: z.string(),
        roles: z.array(z.string()).nullish(),
        firstName: z.string().nullish(),
        lastName: z.string().nullish(),
      }),
    )
    .nullish()
    .catch(undefined),
});

/** Legacy searched 100 at a time; organizations rarely have more teams. */
const TEAM_SEARCH_LIMIT = 200;

/**
 * The organization's teams through core-api's team search, which hides
 * other teams from regular members when the organization's teams are
 * private.
 */
export const getBrowseTeams = createServerFn().handler(
  async (): Promise<BrowseTeams> => {
    const actor = await loadSettingsActor();
    const response = await serverFetch({
      path: `${orgPath(actor)}/teams/search`,
      method: 'POST',
      userId: actor.userId,
      body: JSON.stringify({ limit: TEAM_SEARCH_LIMIT, skip: 0 }),
    });
    if (!response.ok) {
      throw new Error(`core-api team search responded ${response.status}`);
    }
    const json: unknown = await response.json();
    const items =
      json && typeof json === 'object' && 'items' in json ? json.items : [];

    const teams = parseList(items, rawSearchedTeamSchema).flatMap((team) => {
      const id = team.id ?? team._id;
      if (!id) return [];
      const roles = rolesFromRecords({
        userId: actor.userId,
        organizationUsers: actor.organization.users,
        teamMembers: team.members,
      });
      const owner = team.members?.find((m) => m.roles?.includes('owner'));
      return [
        {
          id,
          name: team.name ?? '',
          description: team.description ?? '',
          imageUrl: team.imageUrl || undefined,
          memberCount: team.memberCount ?? team.members?.length ?? 0,
          ...(owner
            ? {
                ownerName:
                  `${owner.firstName ?? ''} ${owner.lastName ?? ''}`.trim(),
              }
            : {}),
          canAddMembers: can('addToTeam', roles),
          canManageMembers: can('changeRoleTeam', roles),
        },
      ];
    });

    return {
      teams: teams.toSorted((a, b) => a.name.localeCompare(b.name)),
      privateTeams: actor.organization.privateTeams ?? false,
      canChangePrivacy: can('changeTeamsPrivacy', actor.roles),
    };
  },
);

const privacyInput = z.object({ privateTeams: z.boolean() });

export const setTeamsPrivacy = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof privacyInput>) => privacyInput.parse(data))
  .handler(({ data }) =>
    guardedMutation('setTeamsPrivacy', async () => {
      const actor = await requireSettingsPermission('changeTeamsPrivacy');
      const response = await serverFetch({
        path: `${orgPath(actor)}/setPrivateTeams`,
        method: 'PUT',
        userId: actor.userId,
        body: JSON.stringify(data),
      });
      return toCoreApiMutationResult(response, 'setTeamsPrivacy');
    }),
  );

// Members

export type OrganizationRole = 'owner' | 'admin' | 'member';

export interface OrganizationMemberRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  picture?: string;
  role: OrganizationRole;
  lastLoginAt?: string;
}

export interface OrganizationMembers {
  currentUserId: string;
  members: OrganizationMemberRow[];
  can: { changeRole: boolean; transferOwnership: boolean; remove: boolean };
}

const rawOrgMemberSchema = z.object({
  id: z.string(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().nullish(),
  picture: z.string().nullish(),
  roles: z.array(z.string()).nullish(),
  lastLoginAt: z.string().nullish(),
});

function orgRoleFrom(
  roles: readonly string[] | null | undefined,
): OrganizationRole {
  if (roles?.includes('owner')) return 'owner';
  if (roles?.includes('admin')) return 'admin';
  return 'member';
}

function toMemberRow(m: z.infer<typeof rawOrgMemberSchema>) {
  return {
    id: m.id,
    firstName: m.firstName ?? '',
    lastName: m.lastName ?? '',
    email: m.email ?? '',
    picture: m.picture || undefined,
    role: orgRoleFrom(m.roles),
    lastLoginAt: m.lastLoginAt || undefined,
  };
}

export const getOrganizationMembers = createServerFn().handler(
  async (): Promise<OrganizationMembers> => {
    const actor = await loadSettingsActor();
    const members = parseList(
      await fetchJson(`${orgPath(actor)}/members`, actor.userId),
      rawOrgMemberSchema,
    );
    return {
      currentUserId: actor.userId,
      members: members.map((m) => toMemberRow(m)).toSorted(byFullName),
      can: {
        changeRole: can('changeRoleOrg', actor.roles),
        transferOwnership: can('transferOrgOwnership', actor.roles),
        remove: can('removeFromOrg', actor.roles),
      },
    };
  },
);

const ORG_ROLE_RECORDS: Record<OrganizationRole, string[]> = {
  owner: ['owner', 'member'],
  admin: ['admin', 'member'],
  member: ['member'],
};

async function writeOrgRole(
  actor: SettingsActor,
  userId: string,
  role: OrganizationRole,
): Promise<SettingsMutationResult> {
  const response = await serverFetch({
    path: `${orgPath(actor)}/members/${encodeURIComponent(userId)}/roles`,
    method: 'PUT',
    userId: actor.userId,
    body: JSON.stringify({ roles: ORG_ROLE_RECORDS[role] }),
  });
  return toCoreApiMutationResult(response, 'writeOrgRole');
}

function orgRoleOf(actor: SettingsActor, userId: string) {
  const member = actor.organization.users?.find((u) => u.userId === userId);
  return member ? orgRoleFrom(member.roles) : undefined;
}

const memberInput = z.object({ userId: objectId });
const changeRoleInput = memberInput.extend({
  role: z.enum(['admin', 'member']),
});

export const changeOrganizationMemberRole = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof changeRoleInput>) =>
    changeRoleInput.parse(data),
  )
  .handler(({ data }) =>
    guardedMutation('changeOrganizationMemberRole', async () => {
      const actor = await requireSettingsPermission('changeRoleOrg');
      const current = orgRoleOf(actor, data.userId);
      if (!current) {
        return { success: false, message: 'Not in this organization.' };
      }
      if (current === 'owner') {
        return {
          success: false,
          message: 'Transfer ownership to change the owner.',
        };
      }
      if (data.userId === actor.userId) {
        return { success: false, message: "You can't change your own role." };
      }
      if (current === data.role) return { success: true };
      return writeOrgRole(actor, data.userId, data.role);
    }),
  );

export const transferOrganizationOwnership = createServerFn({
  method: 'POST',
})
  .validator((data: z.infer<typeof memberInput>) => memberInput.parse(data))
  .handler(({ data }) =>
    guardedMutation('transferOrganizationOwnership', async () => {
      const actor = await requireSettingsPermission('transferOrgOwnership');
      const current = orgRoleOf(actor, data.userId);
      if (!current) {
        return { success: false, message: 'Not in this organization.' };
      }
      if (current === 'owner') return { success: true };
      const promoted = await writeOrgRole(actor, data.userId, 'owner');
      if (!promoted.success) return promoted;
      // As in legacy, the previous owner (the only one allowed to transfer)
      // becomes an admin.
      return writeOrgRole(actor, actor.userId, 'admin');
    }),
  );

export const removeOrganizationMember = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof memberInput>) => memberInput.parse(data))
  .handler(({ data }) =>
    guardedMutation('removeOrganizationMember', async () => {
      const actor = await requireSettingsPermission('removeFromOrg');
      const current = orgRoleOf(actor, data.userId);
      if (!current) return { success: true };
      if (current === 'owner') {
        return {
          success: false,
          message: 'Transfer ownership before removing the owner.',
        };
      }
      if (data.userId === actor.userId) {
        return {
          success: false,
          message: "You can't remove yourself from the organization.",
        };
      }
      const response = await serverFetch({
        path: `${orgPath(actor)}/members/${encodeURIComponent(data.userId)}`,
        method: 'DELETE',
        userId: actor.userId,
      });
      return toCoreApiMutationResult(response, 'removeOrganizationMember');
    }),
  );

// Invitations and unassigned users

export interface InvitableTeam {
  id: string;
  name: string;
}

export interface PendingInvitation {
  /** Opaque key for deleting; only present for those allowed to delete. */
  key?: string;
  email: string;
  role: OrganizationRole;
  createdAt?: string;
}

export interface OrganizationInvitations {
  /** Teams the researcher may invite people to. */
  teams: InvitableTeam[];
  canInviteAdmins: boolean;
  invitations: PendingInvitation[];
  unassigned: Omit<OrganizationMemberRow, 'role' | 'lastLoginAt'>[];
  canRemoveFromOrg: boolean;
  /** Legacy showed "Unassigned users" in place of invitations under SSO. */
  hasEnabledSsoProvider: boolean;
}

const rawInvitationSchema = z.object({
  token: z.string(),
  email: z.string().nullish(),
  status: z.string().nullish(),
  roles: z.array(z.string()).nullish(),
  createdAt: z.string().nullish(),
});

const rawTeamWithMembersSchema = z.object({
  id: z.string().optional(),
  _id: z.string().optional(),
  name: z.string().nullish(),
  members: z
    .array(
      z.object({
        userId: z.string(),
        roles: z.array(z.string()).nullish(),
        isActive: z.boolean().nullish(),
      }),
    )
    .nullish()
    .catch(undefined),
});

/** Teams in the organization the researcher may add people to. */
async function invitableTeams(actor: SettingsActor): Promise<InvitableTeam[]> {
  const teams = parseList(
    await fetchJson(
      `/v4/organizations/${encodeURIComponent(actor.organizationId)}/teams`,
      actor.userId,
    ),
    rawTeamWithMembersSchema,
  );
  return teams
    .flatMap((team) => {
      const id = team.id ?? team._id;
      if (!id) return [];
      const roles = rolesFromRecords({
        userId: actor.userId,
        organizationUsers: actor.organization.users,
        teamMembers: team.members,
      });
      return can('addToTeam', roles) ? [{ id, name: team.name ?? '' }] : [];
    })
    .toSorted((a, b) => a.name.localeCompare(b.name));
}

const rawInvitationsDocSchema = z.object({
  invitations: z.array(z.unknown()).nullish().catch(undefined),
});

export const getOrganizationInvitations = createServerFn().handler(
  async (): Promise<OrganizationInvitations> => {
    const actor = await loadSettingsActor();
    const canRemoveFromOrg = can('removeFromOrg', actor.roles);
    const [teams, unassignedJson] = await Promise.all([
      invitableTeams(actor),
      fetchJson(`${orgPath(actor)}/members/unassigned`, actor.userId),
    ]);
    const invitations = parseList(
      rawInvitationsDocSchema.parse(actor.organization).invitations,
      rawInvitationSchema,
    )
      .filter((invite) => !invite.status || invite.status === 'pending')
      .map((invite) => ({
        // The token is the invitation's acceptance secret; only those who may
        // delete invitations get it, and only as the key to do so.
        key: canRemoveFromOrg ? invite.token : undefined,
        email: invite.email ?? '',
        role: orgRoleFrom(invite.roles),
        createdAt: invite.createdAt || undefined,
      }));

    return {
      teams,
      canInviteAdmins: can('changeRoleOrg', actor.roles),
      invitations,
      unassigned: parseList(unassignedJson, rawOrgMemberSchema)
        .map(({ id, firstName, lastName, email, picture }) => ({
          id,
          firstName: firstName ?? '',
          lastName: lastName ?? '',
          email: email ?? '',
          picture: picture || undefined,
        }))
        .toSorted(byFullName),
      canRemoveFromOrg,
      hasEnabledSsoProvider:
        actor.organization.ssoProviders?.some((p) => p.enabled === true) ??
        false,
    };
  },
);

const inviteInput = z.object({
  invitations: z
    .array(
      z.object({
        email: z.email().trim().toLowerCase(),
        role: z.enum(['admin', 'member']),
        teamIds: z.array(objectId).min(1),
      }),
    )
    .min(1)
    .max(20),
});

export type InviteResult =
  | {
      success: true;
      sent: number;
      failed: { email: string; message?: string }[];
    }
  | { success: false; message?: string };

/**
 * Sends each invitation. As in legacy, anyone may invite people to teams
 * they can add members to; inviting an admin needs the org role permission.
 */
export const inviteToOrganization = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof inviteInput>) => inviteInput.parse(data))
  .handler(async ({ data }): Promise<InviteResult> => {
    try {
      const actor = await loadSettingsActor();
      const teams = await invitableTeams(actor);
      const allowed = new Set(teams.map((t) => t.id));
      const canInviteAdmins = can('changeRoleOrg', actor.roles);
      for (const invite of data.invitations) {
        if (invite.role === 'admin' && !canInviteAdmins) {
          return { success: false, message: "You can't invite admins." };
        }
        if (invite.teamIds.some((id) => !allowed.has(id))) {
          return {
            success: false,
            message: "You can't add people to one of those teams.",
          };
        }
      }

      let sent = 0;
      const failed: { email: string; message?: string }[] = [];
      for (const invite of data.invitations) {
        // One at a time, as each sends an email and may fail on its own.
        // eslint-disable-next-line no-await-in-loop
        const response = await serverFetch({
          path: `${orgPath(actor)}/invitations`,
          method: 'POST',
          userId: actor.userId,
          body: JSON.stringify({
            userId: actor.userId,
            email: invite.email,
            roles: ORG_ROLE_RECORDS[invite.role],
            resend: false,
            teams: invite.teamIds,
          }),
        });
        // eslint-disable-next-line no-await-in-loop
        const result = await toCoreApiMutationResult(
          response,
          'inviteToOrganization',
        );
        if (result.success) sent += 1;
        else failed.push({ email: invite.email, message: result.message });
      }
      return { success: true, sent, failed };
    } catch (error) {
      console.error('inviteToOrganization: failed', error);
      return { success: false };
    }
  });

const invitationKeyInput = z.object({ key: z.string().min(1).max(200) });

export const deleteInvitation = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof invitationKeyInput>) =>
    invitationKeyInput.parse(data),
  )
  .handler(({ data }) =>
    guardedMutation('deleteInvitation', async () => {
      const actor = await requireSettingsPermission('removeFromOrg');
      const response = await serverFetch({
        path: `${orgPath(actor)}/invitations/${encodeURIComponent(data.key)}`,
        method: 'DELETE',
        userId: actor.userId,
      });
      return toCoreApiMutationResult(response, 'deleteInvitation');
    }),
  );

/** Teams an unassigned user can be put on by this researcher. */
export const getAssignableTeams = createServerFn().handler(
  async (): Promise<InvitableTeam[]> => {
    const actor = await loadSettingsActor();
    return invitableTeams(actor);
  },
);

// SSO

export interface SsoProvider {
  id: string;
  name: string;
  ssoUrl: string;
  certificate: string;
  issuer: string;
  enabled: boolean;
}

export interface SsoDomain {
  id: string;
  domain: string;
  verified: boolean;
}

const rawProviderSchema = z.object({
  id: z.string().optional(),
  _id: z.string().optional(),
  name: z.string().nullish(),
  ssoUrl: z.string().nullish(),
  certificate: z.string().nullish(),
  issuer: z.string().nullish(),
  enabled: z.boolean().nullish(),
});

const rawProvidersDocSchema = z.object({
  ssoProviders: z.array(z.unknown()).nullish().catch(undefined),
});

export const getSsoProviders = createServerFn().handler(
  async (): Promise<SsoProvider[]> => {
    const actor = await requireSettingsPermission('manageSso');
    return parseList(
      rawProvidersDocSchema.parse(actor.organization).ssoProviders,
      rawProviderSchema,
    ).flatMap((p) => {
      const id = p.id ?? p._id;
      return id
        ? [
            {
              id,
              name: p.name ?? '',
              ssoUrl: p.ssoUrl ?? '',
              certificate: p.certificate ?? '',
              issuer: p.issuer ?? '',
              enabled: p.enabled ?? false,
            },
          ]
        : [];
    });
  },
);

const providerFields = z.object({
  name: z.string().trim().min(1).max(200),
  ssoUrl: z.string().trim().url(),
  certificate: z.string().trim().min(1),
  issuer: z.string().trim().max(500),
});

export const createSsoProvider = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof providerFields>) =>
    providerFields.parse(data),
  )
  .handler(({ data }) =>
    guardedMutation('createSsoProvider', async () => {
      const actor = await requireSettingsPermission('manageSso');
      const response = await serverFetch({
        path: `${orgPath(actor)}/sso/`,
        method: 'POST',
        userId: actor.userId,
        body: JSON.stringify(data),
      });
      return toCoreApiMutationResult(response, 'createSsoProvider');
    }),
  );

const providerUpdate = providerFields.extend({
  providerId: objectId,
  enabled: z.boolean(),
});

export const updateSsoProvider = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof providerUpdate>) =>
    providerUpdate.parse(data),
  )
  .handler(({ data }) =>
    guardedMutation('updateSsoProvider', async () => {
      const actor = await requireSettingsPermission('manageSso');
      const { providerId, ...fields } = data;
      const response = await serverFetch({
        path: `${orgPath(actor)}/sso/${encodeURIComponent(providerId)}`,
        method: 'PUT',
        userId: actor.userId,
        body: JSON.stringify(fields),
      });
      return toCoreApiMutationResult(response, 'updateSsoProvider');
    }),
  );

const providerIdInput = z.object({ providerId: objectId });

export const deleteSsoProvider = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof providerIdInput>) =>
    providerIdInput.parse(data),
  )
  .handler(({ data }) =>
    guardedMutation('deleteSsoProvider', async () => {
      const actor = await requireSettingsPermission('manageSso');
      const response = await serverFetch({
        path: `${orgPath(actor)}/sso/${encodeURIComponent(data.providerId)}`,
        method: 'DELETE',
        userId: actor.userId,
      });
      return toCoreApiMutationResult(response, 'deleteSsoProvider');
    }),
  );

const rawDomainSchema = z.object({
  id: z.string().optional(),
  _id: z.string().optional(),
  domain: z.string().nullish(),
  verified: z.boolean().nullish(),
});

export const getSsoDomains = createServerFn().handler(
  async (): Promise<SsoDomain[]> => {
    const actor = await requireSettingsPermission('manageSso');
    return parseList(
      await fetchJson(`${orgPath(actor)}/domains`, actor.userId),
      rawDomainSchema,
    )
      .flatMap((d) => {
        const id = d.id ?? d._id;
        return id
          ? [{ id, domain: d.domain ?? '', verified: d.verified ?? false }]
          : [];
      })
      .toSorted((a, b) => a.domain.localeCompare(b.domain));
  },
);

const domainInput = z.object({
  domain: z
    .string()
    .trim()
    .toLowerCase()
    .regex(SSO_CLAIMED_DOMAIN_REGEX, 'Enter a domain like acme.com'),
});

export const addSsoDomain = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof domainInput>) => domainInput.parse(data))
  .handler(({ data }) =>
    guardedMutation('addSsoDomain', async () => {
      const actor = await requireSettingsPermission('manageSso');
      const response = await serverFetch({
        path: `${orgPath(actor)}/domains`,
        method: 'POST',
        userId: actor.userId,
        body: JSON.stringify({ domain: data.domain }),
      });
      return toCoreApiMutationResult(response, 'addSsoDomain');
    }),
  );

const domainUpdate = z.object({ domainId: objectId, verified: z.boolean() });

export const setSsoDomainEnabled = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof domainUpdate>) => domainUpdate.parse(data))
  .handler(({ data }) =>
    guardedMutation('setSsoDomainEnabled', async () => {
      const actor = await requireSettingsPermission('manageSso');
      const response = await serverFetch({
        path: `${orgPath(actor)}/domains/${encodeURIComponent(data.domainId)}`,
        method: 'PUT',
        userId: actor.userId,
        body: JSON.stringify({ verified: data.verified }),
      });
      return toCoreApiMutationResult(response, 'setSsoDomainEnabled');
    }),
  );

const domainIdInput = z.object({ domainId: objectId });

export const deleteSsoDomain = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof domainIdInput>) => domainIdInput.parse(data))
  .handler(({ data }) =>
    guardedMutation('deleteSsoDomain', async () => {
      const actor = await requireSettingsPermission('manageSso');
      // core-api serves this one under the singular `/organization/` path.
      const response = await serverFetch({
        path: `/organization/${encodeURIComponent(actor.organizationId)}/domains/${encodeURIComponent(data.domainId)}`,
        method: 'DELETE',
        userId: actor.userId,
      });
      return toCoreApiMutationResult(response, 'deleteSsoDomain');
    }),
  );
